import React from 'react'
import { useNavigate } from 'react-router-dom'

import { toDollar } from 'utils'
import { useCart, cartItemTotal, cartSetupFeeTotal, cartItemPerUnitSurcharge } from 'contexts/CartContext'
import { ROUTES } from 'routes'

import { MinusIcon, PlusIcon, ShoppingCartIcon, Trash2Icon } from 'lucide-react'
import { alpha, Box, Divider, Grid, IconButton, Stack, Typography, useTheme } from '@mui/material'

import { Button, Breadcrumbs, Card, Skeleton } from '@repo/ui'
import { EmptyState } from 'components'


// ─── Cart line row ─────────────────────────────────────────────────────────────

function CartLineRow({ productId }: { productId: string }) {
  const { items, removeItem, setQuantity } = useCart()
  const item = items.find(i => i.productId === productId)
  const theme = useTheme()

  if (!item) return null

  return (
    <Card sx={{ p: 4 }}>
      <Stack direction="row" alignItems="center" spacing={4}>
        {/* Square product thumbnail */}
        {item.image ? (
          <Box
            component="img"
            src={item.image}
            alt={item.title}
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              objectFit: 'cover',
              flexShrink: 0,
              border: `1px solid ${theme.palette.primary[100]}`,
            }}
          />
        ) : (
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              flexShrink: 0,
              backgroundColor: theme.palette.grey[100],
            }}
          />
        )}

        {/* Title + quantity + add-ons */}
        <Stack spacing={1} minWidth={0}>
          <Typography variant="body2" noWrap>{item.title}</Typography>
          <Typography variant="body3" color="text.secondary">
            Quantity: {item.variantTitle}
          </Typography>
          {(item.customization || item.onMetal) && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              {item.customization && (
                <Typography
                  variant="body4"
                  sx={{ px: 1.5, py: 0.25, borderRadius: 1, backgroundColor: theme.palette.primary[100], color: 'text.secondary' }}
                >
                  {item.customization.qr ? 'Customization + QR' : 'Customization'}
                </Typography>
              )}
              {item.onMetal && (
                <Typography
                  variant="body4"
                  sx={{ px: 1.5, py: 0.25, borderRadius: 1, backgroundColor: theme.palette.primary[100], color: 'text.secondary' }}
                >
                  On-metal
                </Typography>
              )}
            </Stack>
          )}
        </Stack>

        {/* Quantity stepper — its own column (right of the title/quantity) so the
            row reads product → quantity → price → remove. */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          <IconButton
            size="small"
            onClick={() => setQuantity(item.productId, item.quantity - 1)}
            disabled={item.quantity <= 1}
            aria-label="Decrease quantity"
            sx={{ border: `1px solid ${theme.palette.primary[100]}`, borderRadius: 1, p: 0.5 }}
          >
            <MinusIcon size={14} strokeWidth={1.5} />
          </IconButton>
          <Typography variant="body2" minWidth={24} textAlign="center">
            {item.quantity}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setQuantity(item.productId, item.quantity + 1)}
            aria-label="Increase quantity"
            sx={{ border: `1px solid ${theme.palette.primary[100]}`, borderRadius: 1, p: 0.5 }}
          >
            <PlusIcon size={14} strokeWidth={1.5} />
          </IconButton>
        </Stack>

        {/* Spacer — pushes price + remove to the far right, keeping the stepper
            next to the product/quantity on the left. */}
        <Box sx={{ flex: 1 }} />

        {/* Price — line total incl. add-ons, with a breakdown when any apply. */}
        <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Typography variant="body2">{toDollar(cartItemTotal(item))}</Typography>
          <Typography variant="body3" color="text.secondary">
            {toDollar(item.unitPrice)} per unit
          </Typography>
          {/* VRG-575: setup is no longer itemized per line — it is charged once
              per artwork across the whole cart, so it is shown at cart level. */}
          {cartItemPerUnitSurcharge(item) > 0 && (
            <Typography variant="body4" color="text.secondary">
              incl. {toDollar(cartItemPerUnitSurcharge(item))} on-metal
            </Typography>
          )}
        </Stack>

        {/* Remove — trash is destructive on hover/focus only (§25) */}
        <IconButton
          onClick={() => removeItem(item.productId)}
          aria-label="Remove item"
          sx={{
            flexShrink: 0,
            color: theme.palette.text.secondary,
            '&:hover, &:focus-visible': {
              color: theme.palette.error.main,
              backgroundColor: alpha(theme.palette.error.main, 0.08),
            },
          }}
        >
          <Trash2Icon size={18} strokeWidth={1.5} />
        </IconButton>
      </Stack>
    </Card>
  )
}


// ─── Cart skeleton ─────────────────────────────────────────────────────────────

function CartSkeleton() {
  return (
    <Grid container spacing={6}>
      <Grid item xs={12} md={8}>
        <Stack spacing={0} divider={<Divider />}>
          {[1, 2, 3].map(i => (
            <Stack key={i} direction="row" alignItems="flex-start" justifyContent="space-between" py={3} spacing={4}>
              <Skeleton width={72} height={72} />
              <Stack flex={1} spacing={2}>
                <Skeleton width="60%" height={20} />
                <Skeleton width="40%" height={16} />
                <Skeleton width={100} height={32} />
              </Stack>
              <Stack alignItems="flex-end" spacing={2}>
                <Skeleton width={80} height={20} />
                <Skeleton width={60} height={16} />
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Grid>
      <Grid item xs={12} md={4}>
        <Stack spacing={4} sx={{ p: 4, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
          <Skeleton width="50%" height={24} />
          <Skeleton height={20} />
          <Skeleton height={20} />
          <Skeleton height={44} />
        </Stack>
      </Grid>
    </Grid>
  )
}


// ─── Page ──────────────────────────────────────────────────────────────────────

export const TagsShopCartPage = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { items, subtotal } = useCart()
  // VRG-575: `subtotal` already carries the cart-wide setup fee, so the items
  // row shows it net — otherwise the fee would appear twice on the way to a
  // total that only counted it once.
  const setupFee = cartSetupFeeTotal(items)
  const itemsSubtotal = subtotal - setupFee

  const isEmpty = items.length === 0

  return (
    <>
      {/* Header — breadcrumbs are the exit affordance ("Shop" returns to the
          grid); per-row trash handles removal. No redundant Continue-shopping /
          Clear-cart buttons. */}
      <Stack pt={2} direction="row" pb={6}>
        <Breadcrumbs />
      </Stack>

      {isEmpty ? (
        <EmptyState
          title="Your cart is empty"
          text="Browse the tag shop and add items to get started."
          icon={<ShoppingCartIcon strokeWidth={1} size={32} />}
          buttons={
            <Button onClick={() => navigate(ROUTES.TAGS_SHOP)}>
              Browse tags
            </Button>
          }
        />
      ) : (
        <Grid container spacing={6}>
          {/* Line items */}
          <Grid item xs={12} md={8}>
            <Stack spacing={0.5} pb={4}>
              {/* Match the standard page-title size (Header: Lato 24px/600) and
                  move the count to a smaller subtitle (Figma: "N products in your cart"). */}
              <Typography
                sx={{
                  fontFamily: 'Lato, Montserrat, sans-serif',
                  fontSize: '24px',
                  fontWeight: 600,
                  lineHeight: '130%',
                }}
              >
                Cart
              </Typography>
              <Typography variant="body3" color="text.secondary">
                {items.length} {items.length === 1 ? 'product' : 'products'} in your cart
              </Typography>
            </Stack>
            <Stack spacing={4}>
              {items.map(item => (
                <CartLineRow key={item.productId} productId={item.productId} />
              ))}
            </Stack>
          </Grid>

          {/* Order summary */}
          <Grid item xs={12} md={4}>
            <Stack
              spacing={4}
              sx={{
                p: 6,
                // Match the order-detail Order summary exactly (the InfoBlock
                // treatment): primary[50] fill, borderless, 12px radius, p:6 padding.
                // The two Order summary panels are the same object and must look
                // identical.
                backgroundColor: theme.palette.primary[50],
                border: `1px solid ${theme.palette.primary[50]}`,
                borderRadius: 3,
                position: 'sticky',
                top: 24,
              }}
            >
              <Typography variant="body1">Order summary</Typography>

              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Subtotal
                  </Typography>
                  <Typography variant="body2">{toDollar(itemsSubtotal)}</Typography>
                </Stack>
                {/* VRG-575: one fee per artwork, cart-wide. Shown as its own row
                    so a customer can see it is charged once, not per line. */}
                {setupFee > 0 && (
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack sx={{ pr: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Customization setup
                      </Typography>
                      <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        Charged once per artwork. Reordering the same design is not
                        charged again.
                      </Typography>
                    </Stack>
                    <Typography variant="body2">{toDollar(setupFee)}</Typography>
                  </Stack>
                )}
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Shipping
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Calculated at checkout
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Total</Typography>
                  <Typography variant="body2">{toDollar(subtotal)}</Typography>
                </Stack>
              </Stack>

              <Button
                fullWidth
                onClick={() => navigate(ROUTES.TAGS_SHOP_CHECKOUT)}
                disabled={isEmpty}
              >
                Checkout
              </Button>
            </Stack>
          </Grid>
        </Grid>
      )}
    </>
  )
}
